import React, { useCallback, useMemo, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { FiAlertTriangle } from 'react-icons/fi';
import { UiPen } from '@vyaguta/vyaguta-icons/ui';

import LFModal from 'components/common/LFModal/LFModal';
import { useDeleteQuickLinksCategoryMutation } from 'hooks/query/useQuickLinksQuery';

import * as toast from 'utils/toast';

import AddCategory from 'pages/eventsAndNotices/linkLibrary/components/AddCategory';
import DeletePopup from 'pages/eventsAndNotices/linkLibrary/components/DeletePopup';

import { LinkLibraryNode } from 'interface/linkLibraryTree';

type LinkLibraryCategoryNode = LinkLibraryNode & { id: number | string };

interface Props {
  selectedCategory: LinkLibraryCategoryNode;
  handleRowClick?: () => void;
  popoverId?: string;
  setActivePopoverId?: React.Dispatch<React.SetStateAction<string | null>>;
}

const CategoryVerticalEllipsis: React.FC<Props> = (props) => {
  const { selectedCategory, handleRowClick, popoverId, setActivePopoverId } = props;
  const { mutateAsync: deleteQuickLinksCategory } = useDeleteQuickLinksCategoryMutation();

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isCategoryEditModalOpen, setIsCategoryEditModalOpen] = useState(false);

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
  };

  const closeCategoryEditModal = () => {
    setIsCategoryEditModalOpen(false);
  };

  const closePopover = useCallback(() => {
    if (!setActivePopoverId || !popoverId) {
      return;
    }

    setActivePopoverId((current) => (current === popoverId ? null : current));
  }, [popoverId, setActivePopoverId]);

  const deleteCategory = async () => {
    const data = (await deleteQuickLinksCategory(selectedCategory.id)) as { message: string };

    toast.success({
      title: 'Success',
      message: data.message,
    });
  };

  const EditCategory = useMemo<React.FC<{ onClick: () => void }>>(
    () =>
      function EditCategoryButton({ onClick }: { onClick: () => void }) {
        return (
          <li
            className="overflow-menu__list-item overflow-menu__list-item--hoverable d-flex"
            onClick={() => {
              closePopover();
              onClick();
              setIsCategoryEditModalOpen(true);
            }}
          >
            <span className="icon overflow-menu__icon">
              <UiPen className="grey--text" size={16} />
            </span>
            <span>Edit</span>
          </li>
        );
      },
    [closePopover, setIsCategoryEditModalOpen]
  );

  const AddLinkSubCategory = useMemo<React.FC<{ onClick: () => void }>>(
    () =>
      function AddLinkSubCategoryButton({ onClick }: { onClick: () => void }) {
        return (
          <li
            className="overflow-menu__list-item overflow-menu__list-item--hoverable d-flex"
            onClick={() => {
              closePopover();
              onClick();
              setIsCategoryModalOpen(true);
            }}
          >
            <span className="icon overflow-menu__icon">
              <MdAdd className="grey--text" size={16} />
            </span>
            <span>Add new sub-category</span>
          </li>
        );
      },
    [closePopover, setIsCategoryModalOpen]
  );

  const DeleteCategoryTrigger = useMemo<React.FC<{ onClick: () => void }>>(
    () =>
      function DeleteCategoryTriggerButton({ onClick }: { onClick: () => void }) {
        return (
          <DeletePopup
            onClick={(e) => {
              e.preventDefault();
              closePopover();
              onClick();
            }}
          />
        );
      },
    [closePopover]
  );

  return (
    <div className="overflow-menu">
      <div className="overflow-menu__block">
        <ul className="overflow-menu__list">
          <AddCategory
            isEdit={true}
            key="editCategory"
            isOpen={isCategoryEditModalOpen}
            onClose={closeCategoryEditModal}
            isSubCategory={false}
            selectedCategory={selectedCategory}
            TriggerBtn={EditCategory}
          />
          <AddCategory
            key="addCategory"
            isEdit={false}
            isSubCategory={true}
            isOpen={isCategoryModalOpen}
            onClose={closeCategoryModal}
            selectedCategory={selectedCategory}
            TriggerBtn={AddLinkSubCategory}
            handleRowClick={handleRowClick}
          />
          <LFModal
            TriggerBtn={DeleteCategoryTrigger}
            onClose={closePopover}
            modalStatusIcon={<FiAlertTriangle color="#F44336" size={24} />}
            title={{ text: `Delete category ${selectedCategory.name}`, type: 'danger' }}
            className="delete-modal"
            htmlContent={
              <div className="d-grid mbpx-4">
                <span className="delete__text">
                  This action cannot be reverted back. The category {selectedCategory.name} will be deleted with the
                  links or category under it.
                </span>
              </div>
            }
            renderFooter={true}
            buttons={[
              {
                text: 'Yes, delete',
                type: 'red',
                className: 'mr-12 delete__btn',
                onClick: deleteCategory,
                close: true,
                showLoading: true,
              },
              {
                text: 'No, go back',
                type: 'outlined-grey',
                className: 'py-8 px-12',
                close: true,
              },
            ]}
          />
        </ul>
      </div>
    </div>
  );
};

export default CategoryVerticalEllipsis;
